import { Offer } from 'models';
import Points from 'components/Points';
import { Typography, Button } from '@mui/material';

import {
  StyledModal,
  StyledContent,
  StyledTertiaryButton,
  PurchaseSummary,
  SummaryText,
  Message,
  StyledErrorIcon,
  StyledBox,
  Spinner,
} from './PurchaseVoucherModal.styles';

interface ErrorProps {
  onClose: () => void;
  onTryAgain: () => void;
}

interface ConfirmProps {
  offer: Offer | null;
  onCancel: () => void;
  onConfirm: () => void;
}

interface CurrentViewProps extends ConfirmProps {
  onClose: () => void;
  isLoading: boolean;
  showError: boolean;
}

export interface PurchaseVoucherModalProps extends CurrentViewProps {
  isOpen: boolean;
}

const Loading = () => (
  <Message>
    <Typography variant="h2" gutterBottom>
      Relax - we&apos;re loading your tasty Perkz
    </Typography>
    <Spinner />
  </Message>
);

const Error = ({ onClose, onTryAgain }: ErrorProps) => (
  <Message>
    <Typography variant="h2">Oops, that Perk purchase didn&apos;t go through</Typography>
    <StyledErrorIcon />
    <Button fullWidth variant="contained" onClick={onTryAgain}>
      Try again
    </Button>
    <StyledTertiaryButton fullWidth onClick={onClose}>
      Back to Perkz store
    </StyledTertiaryButton>
  </Message>
);

const Confirm = ({ offer, onConfirm, onCancel }: ConfirmProps) => {
  const { title, points = 0 } = offer || {};

  return (
    <>
      <Typography variant="h2">You&apos;re almost there...ready to purchase?</Typography>
      <StyledBox>
        <Typography>Once purchased, you can&apos;t swap your Perkz back to Zs.</Typography>
      </StyledBox>
      <PurchaseSummary>
        <SummaryText variant="h5">{title}</SummaryText>
        <Points points={points} />
      </PurchaseSummary>
      <Button fullWidth color="primary" variant="contained" onClick={onConfirm}>
        Purchase now
      </Button>
      <StyledTertiaryButton fullWidth color="error" onClick={onCancel}>
        Nope, take me back
      </StyledTertiaryButton>
    </>
  );
};

const CurrentView = (props: CurrentViewProps) => {
  const { offer, isLoading, showError, onConfirm, onClose, onCancel } = props;

  if (isLoading) {
    return <Loading />;
  }

  if (showError) {
    return <Error onClose={onClose} onTryAgain={onConfirm} />;
  }

  return <Confirm offer={offer} onConfirm={onConfirm} onCancel={onCancel} />;
};

export const PurchaseVoucherModal = (props: PurchaseVoucherModalProps) => {
  const { isOpen, onClose } = props;

  return (
    <StyledModal open={isOpen} onClose={onClose} closeType="none" aria-label="Purchase Perk Modal">
      <StyledContent>
        <CurrentView {...props} />
      </StyledContent>
    </StyledModal>
  );
};
